import React, { useMemo } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Renders a form from a doc-type's `fields` schema (from GET /doc-types/:slug).
 * This is the piece that makes adding document type #N free on the frontend —
 * every field type (text/textarea/date/number/select) maps to a shared input,
 * grouped by `field.group` into labeled sections.
 */
const DocFormFields = ({ fields = [], values = {}, onChange, errors = {} }) => {
  const groups = useMemo(() => {
    const map = new Map();
    for (const field of fields) {
      const key = field.group || "Details";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(field);
    }
    return Array.from(map.entries());
  }, [fields]);

  const setField = (key, value) => onChange({ ...values, [key]: value });

  return (
    <div className="space-y-6">
      {groups.map(([group, groupFields]) => (
        <div key={group}>
          <h4 className="text-[10px] uppercase tracking-[0.16em] text-white/45 mb-3">{group}</h4>
          <div className="space-y-3">
            {groupFields.map((field) => (
              <div key={field.key}>
                <label className="field-label">
                  {field.label} {field.required && <span className="text-[#d4a54b]">*</span>}
                </label>
                <FieldInput
                  field={field}
                  value={values[field.key]}
                  onChange={(v) => setField(field.key, v)}
                />
                {field.help && <p className="text-[11px] text-white/35 mt-1">{field.help}</p>}
                {errors[field.key] && <p className="text-[11px] text-red-400 mt-1">{errors[field.key]}</p>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const FieldInput = ({ field, value, onChange }) => {
  const current = value ?? field.default ?? "";

  if (field.type === "textarea") {
    return (
      <textarea
        rows={3}
        className="field"
        value={current}
        placeholder={field.placeholder || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (field.type === "select") {
    return (
      <div className="relative">
        <select
          className="field appearance-none pr-9 cursor-pointer"
          value={current}
          onChange={(e) => onChange(e.target.value)}
        >
          {!field.options?.includes(current) && current === "" && (
            <option value="" className="bg-[#0e0c12]">Select…</option>
          )}
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt} className="bg-[#0e0c12]">{opt}</option>
          ))}
        </select>
        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
      </div>
    );
  }

  if (field.type === "number") {
    return (
      <input
        type="number"
        className="field"
        value={current}
        placeholder={field.placeholder || ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      />
    );
  }

  if (field.type === "date") {
    return (
      <input
        type="text"
        className="field"
        value={current}
        placeholder={field.placeholder || "April 25, 2026"}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  // "text" and anything unrecognized fall back to a plain text input, so an
  // unknown future field type still renders something usable.
  return (
    <input
      type="text"
      className="field"
      value={current}
      placeholder={field.placeholder || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

export default DocFormFields;
